import type { DeliveriesController_record_v1Body } from '@repo/api-client';
import { z } from 'zod';

import { INSTANT_MALFORMED, isoInstantFrom } from '@/lib/local-instant';

/**
 * What a delivery note is, before it becomes a request.
 *
 * The screen beside this file collects it; this owns the rules, so the sentence
 * a person reads and the check that produced it are the same statement in one
 * place. Every bound below is **published on the operation** —
 * `DeliveryLineDto.quantity` carries `minimum` and `maximum`, and
 * `RecordDeliveryDto.lines` carries `minItems` and `maxItems` — so none of it is
 * a rule this client invented. It is stated here so that a mistake is answered
 * at the field rather than by a round trip that comes back as one message about
 * the whole form.
 *
 * **The API is still what decides.** Everything here is a courtesy: the same
 * values are checked again server-side and refused with a code, and
 * `messageForFailure` is what words that. Nothing in this file writes refusal
 * copy for a server answer.
 *
 * ## The duplicate rule is a property of the note, not of a line
 *
 * `STOCK_DUPLICATE_ITEM_IN_REQUEST` is refused outright and never collapsed
 * (FR-534), because collapsing it would record a level nobody sent. So it cannot
 * be checked per line — in a note naming the same item twice, no single line is
 * wrong — and {@link deliveryLinesSchema} states it on the array, exactly as the
 * API states it on the array property rather than inside its `each` loop.
 *
 * The screen prevents it as well, by taking the items already picked out of
 * every other line's options. Both, deliberately: the picker is what stops it
 * happening, and this is what stops it being *sent* if the picker is ever
 * bypassed — a stale option list, a restored form.
 *
 * ## Quantities are held as text
 *
 * A string rather than a `z.number()`, which is what `priceInRupeesSchema` does
 * and for the same reason: `valueAsNumber` on an empty input is `NaN`, and the
 * resolver then cannot tell a field nobody has typed in from one holding
 * nonsense. Held as text, "you have not filled this in" and "that is not a whole
 * number" stay two different sentences.
 *
 * Whole numbers only, and no sign. The API takes a **positive magnitude** and
 * derives the direction from the route: a delivery adds. There is nowhere on
 * this form to type a minus, and that is the point — a signed figure is how a
 * delivery gets entered as a removal.
 */

/** `maxItems` on `RecordDeliveryDto.lines`. */
export const MAX_DELIVERY_LINES = 500;

/** `maximum` on `DeliveryLineDto.quantity` — a policy bound, well below `int4`. */
export const MAX_DELIVERY_QUANTITY = 1_000_000;

/** Indian digit grouping, as `stock-levels.ts` and `money.ts` use. */
const GROUPING = new Intl.NumberFormat('en-IN');

export const OUTLET_REQUIRED = 'Choose the outlet the goods arrived at.';

export const ARRIVED_REQUIRED = 'Say when the goods arrived.';

export const ITEM_REQUIRED = 'Choose the item this line is for.';

export const ITEM_TWICE =
  'This item is already on another line of this note. Put the whole quantity on one line — a delivery may name each item only once.';

export const QUANTITY_REQUIRED = 'Enter how many of this item arrived.';

export const QUANTITY_MALFORMED =
  'A quantity is a whole number of items — 48, never 48.5 and never a negative figure. A delivery adds to the shelf; there is nothing to sign.';

export const QUANTITY_TOO_SMALL =
  'A delivery moves at least one item. Take the line off the note if none of this item arrived.';

export const QUANTITY_TOO_LARGE = `A delivery line carries at most ${GROUPING.format(
  MAX_DELIVERY_QUANTITY,
)} items. Split the note if a consignment really is larger.`;

/** Digits and nothing else — no sign, no point, no separators. */
const WHOLE_NUMBER = /^\d+$/;

/**
 * How many of one item arrived.
 *
 * Kept here rather than in `lib/field-schemas.ts`, which holds the rules **more
 * than one form** needs. A correction line shares this bound and a stocktake
 * count does not — its floor is zero, because an empty shelf is a real count —
 * so the fragment those screens want is not quite this one, and neither screen
 * exists yet. When the second one lands, this moves and all three take it from
 * one place.
 */
export const deliveryQuantitySchema = z
  .string()
  .trim()
  .min(1, QUANTITY_REQUIRED)
  .regex(WHOLE_NUMBER, QUANTITY_MALFORMED)
  .refine((typed) => Number(typed) >= 1, QUANTITY_TOO_SMALL)
  .refine((typed) => Number(typed) <= MAX_DELIVERY_QUANTITY, QUANTITY_TOO_LARGE);

/**
 * When the goods arrived, as a `datetime-local` field holds it.
 *
 * The **check** is `localInstantSchema`'s — 30 February is not a date and the
 * server refuses one rather than reading it as 2 March — but the required
 * sentence is not: that one asks somebody to choose a moment to look up, which
 * is a different question from when a consignment turned up at the gate. So the
 * rule is imported and only the copy is this form's.
 */
export const arrivedAtSchema = z
  .string()
  .trim()
  .min(1, ARRIVED_REQUIRED)
  .refine((local) => isoInstantFrom(local) !== null, INSTANT_MALFORMED);

/** One line of the note, as the form holds it. */
export const deliveryLineSchema = z.object({
  catalogueItemId: z.string().min(1, ITEM_REQUIRED),
  quantity: deliveryQuantitySchema,
});

/**
 * Every line, with the one rule that is about the note rather than about a line.
 *
 * The duplicate is reported **on the later line's item field**, so the message
 * lands under the control that has to change rather than above the form. The
 * first occurrence is left alone: it is the one the person meant.
 */
export const deliveryLinesSchema = z
  .array(deliveryLineSchema)
  .min(1)
  .max(MAX_DELIVERY_LINES)
  .superRefine((lines, ctx) => {
    const seen = new Set<string>();

    lines.forEach((line, index) => {
      // Case-folded, matching the API's own `@UniqueBy`: RFC 4122 says a reader
      // accepts either case, so an identity comparison would let a pair through
      // that the server then refuses.
      const item = line.catalogueItemId.toLowerCase();

      if (item === '') return;

      if (seen.has(item)) {
        ctx.addIssue({
          code: 'custom',
          message: ITEM_TWICE,
          path: [index, 'catalogueItemId'],
        });

        return;
      }

      seen.add(item);
    });
  });

/** The whole form. The outlet is a field because it is what the request is aimed at. */
export const deliveryNoteSchema = z.object({
  outletId: z.string().min(1, OUTLET_REQUIRED),
  arrivedAt: arrivedAtSchema,
  lines: deliveryLinesSchema,
});

export type DeliveryNoteValues = z.infer<typeof deliveryNoteSchema>;

/** A line as the form opens it: nothing chosen, nothing typed. */
export const blankLine = (): DeliveryNoteValues['lines'][number] => ({
  catalogueItemId: '',
  quantity: '',
});

/**
 * The note as the API takes it **but for its `id`**, or `null` if the moment
 * does not name one.
 *
 * The shape is the generated `…_v1Body` and nothing about this route is written
 * down here. The `id` is left off and added by the caller from its
 * `WriteAttempt`, because it is minted *with* the idempotency key and is
 * therefore a function of what this returns rather than an input to it —
 * `lib/write-attempt.ts` argues that at length.
 *
 * `null` is the impossible case, kept as a value rather than asserted away with
 * a `!`. The resolver has already refused everything it can happen for, so a
 * change that let a malformed moment through would show as a field that refuses
 * rather than as a request carrying `undefined`.
 */
export function deliveryContentFrom(
  values: DeliveryNoteValues,
): Omit<DeliveriesController_record_v1Body, 'id'> | null {
  const occurredAt = isoInstantFrom(values.arrivedAt);

  if (occurredAt === null) return null;

  return {
    occurredAt,
    lines: values.lines.map((line) => ({
      catalogueItemId: line.catalogueItemId,
      // Whole and within bounds by the time it gets here; the resolver is what
      // says so, and it ran before this did.
      quantity: Number(line.quantity),
    })),
  };
}

/**
 * What this form is about to send, as one string.
 *
 * It covers everything the API fingerprints — `@Idempotent` fingerprints
 * `{params, body}`, so the **outlet counts** and not only the body: the same key
 * against a different outlet is a different payload and would be quarantined
 * rather than replayed.
 *
 * The one thing it leaves out is the body's own `id`, and that is not a gap: the
 * id is minted with the key by `attemptFor`, so two attempts that compare equal
 * carry the same id as well as the same key and the raw bodies are identical.
 *
 * `JSON.stringify` over an array rather than an object, so the comparison cannot
 * turn on key order. The same shape as the correction form's, deliberately.
 */
export function fingerprintOf(
  outletId: string,
  content: Omit<DeliveriesController_record_v1Body, 'id'>,
): string {
  return JSON.stringify([outletId, content.occurredAt, content.lines]);
}

/**
 * How many items the note moves in total — the rail's figure.
 *
 * A count of goods and not a money total, so computing it here is not the thing
 * `docs/ui-conventions.md` forbids. Lines that are not yet a whole number
 * contribute nothing rather than `NaN`: this is read while somebody is still
 * typing, and a rail that says "NaN items" on every second keystroke is worse
 * than one that counts up as the note is filled in.
 */
export function unitsOn(lines: readonly { readonly quantity: string }[]): number {
  return lines.reduce((total, line) => {
    const typed = line.quantity.trim();

    return WHOLE_NUMBER.test(typed) ? total + Number(typed) : total;
  }, 0);
}
